/**
 * useDeleteAssessments Hook
 * ORCA360 - Assessment Deletion for Rapid Testing
 *
 * React hook for deleting all assessments from a session to enable rapid regeneration.
 * Preserves session, transcript, speaker assignments, and files.
 */

import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Delete assessments response from API
 */
export interface DeleteAssessmentsResponse {
  success: boolean;
  message: string;
  deletedCounts: {
    pilotAssessments: number;
    instructorAssessments: number;
    validationLogs: number;
    validationJobs: number;
    validationBatches: number;
    debriefingGuides: number;
    debriefingEvents: number;
    debriefingUas: number;
    acknowledgments?: number;
  };
}

/**
 * Hook return type
 */
export interface UseDeleteAssessmentsResult {
  deleteAssessments: (sessionId: string, deleteAcknowledgments?: boolean) => Promise<DeleteAssessmentsResponse>;
  isDeleting: boolean;
  error: string | null;
}

/**
 * Custom hook for deleting assessments
 *
 * @returns Hook with deleteAssessments function, loading state, and error
 *
 * @example
 * ```tsx
 * const { deleteAssessments, isDeleting, error } = useDeleteAssessments();
 *
 * const handleDelete = async () => {
 *   try {
 *     const result = await deleteAssessments(sessionId);
 *     console.log(`Deleted ${result.deletedCounts.pilotAssessments} pilot assessments`);
 *   } catch (err) {
 *     console.error('Failed to delete:', err);
 *   }
 * };
 * ```
 */
export function useDeleteAssessments(): UseDeleteAssessmentsResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAssessments = useCallback(
    async (sessionId: string, deleteAcknowledgments: boolean = false): Promise<DeleteAssessmentsResponse> => {
      setIsDeleting(true);
      setError(null);

      try {
        console.log(`[useDeleteAssessments] Deleting assessments for session ${sessionId}`);
        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token;

        if (!authToken) {
          throw new Error('Authentication required. Please sign in again.');
        }

        const response = await fetch('/api/delete-assessments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            sessionId,
            deleteAcknowledgments,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const result: DeleteAssessmentsResponse = await response.json();

        console.log(`[useDeleteAssessments] Successfully deleted assessments:`, result);

        return result;

      } catch (err: any) {
        const errorMessage = err.message || 'Failed to delete assessments';
        console.error('[useDeleteAssessments] Error:', errorMessage);
        setError(errorMessage);
        throw err;

      } finally {
        setIsDeleting(false);
      }
    },
    []
  );

  return {
    deleteAssessments,
    isDeleting,
    error,
  };
}
